import React from 'react';
import { Link } from 'react-router-dom';
import './PropertyFinancial.css';

const PropertyFinancial = () => {
  return (
    <div className="property-financial-page">
      {/* Hero */}
      <section className="pf-hero">
        <div className="pf-hero__inner">
          <nav className="pf-hero__breadcrumb">
            <Link to="/">Home</Link>
            <span className="pf-hero__breadcrumb-sep">/</span>
            <span className="pf-hero__breadcrumb-current">Property &amp; Financial Affairs LPA</span>
          </nav>
          <h1 className="pf-hero__title">Property &amp; Financial Affairs LPA</h1>
        </div>
      </section>

      {/* What It Covers */}
      <section className="pf-section pf-section--white">
        <div className="pf-section__inner">
          <p className="pf-section__label">Overview</p>
          <h2 className="pf-section__title">What It Covers</h2>
          <p className="pf-section__desc">
            A Property and Financial Affairs LPA authorises your chosen attorneys to manage your
            financial matters on your behalf. This type of LPA covers a broad range of financial
            decisions, including:
          </p>
          <ul className="pf-list">
            <li>Bank accounts and investments</li>
            <li>Household bills and liabilities</li>
            <li>Pensions and benefits</li>
            <li>Buying or selling property</li>
            <li>Dealings with HMRC</li>
            <li>Instructing accountants, financial advisers and solicitors</li>
            <li>Business interests (where applicable)</li>
          </ul>
          <div className="pf-note">
            <p>
              You may include instructions or preferences to guide how your attorneys act. Instructions
              are binding; preferences are advisory but should be followed wherever possible.
            </p>
          </div>
        </div>
      </section>

      {/* What It Does Not Cover */}
      <section className="pf-section pf-section--alt">
        <div className="pf-section__inner">
          <p className="pf-section__label">Limitations</p>
          <h2 className="pf-section__title">What It Does Not Cover</h2>
          <p className="pf-section__desc">
            A Property and Financial Affairs LPA is limited to financial matters. It does not
            extend to decisions about your personal welfare. Specifically, it does not cover:
          </p>
          <ul className="pf-list">
            <li>Healthcare or welfare decisions</li>
            <li>Authority beyond the scope defined in the LPA document</li>
            <li>Substantial gifting (unless expressly authorised within the LPA)</li>
            <li>Overriding your decisions while you retain mental capacity</li>
          </ul>
          <div className="pf-note">
            <p>
              Attorneys must always act in your best interests and comply with the Mental Capacity
              Act 2005. Any decision made outside the scope of the LPA or contrary to the Act may
              be challenged.
            </p>
          </div>
        </div>
      </section>

      {/* When It Takes Effect */}
      <section className="pf-section pf-section--white">
        <div className="pf-section__inner">
          <p className="pf-section__label">Activation</p>
          <h2 className="pf-section__title">When It Takes Effect</h2>
          <p className="pf-section__desc">
            Before a Property and Financial Affairs LPA can be used, it must be registered with
            the Office of the Public Guardian (OPG). Registration is a mandatory step and the
            document has no legal effect until this is complete.
          </p>
          <div className="pf-timeline">
            <div className="pf-timeline__item">
              <div className="pf-timeline__marker">1</div>
              <div className="pf-timeline__content">
                <h4>Registration with the OPG</h4>
                <p>
                  The LPA must be registered before it can be used. This process typically takes
                  several weeks once the application has been submitted.
                </p>
              </div>
            </div>
            <div className="pf-timeline__item">
              <div className="pf-timeline__marker">2</div>
              <div className="pf-timeline__content">
                <h4>Use with your consent</h4>
                <p>
                  Once registered, your attorneys may act on your behalf with your consent while you
                  retain mental capacity. This can be helpful for practical convenience, such as
                  managing day-to-day banking.
                </p>
              </div>
            </div>
            <div className="pf-timeline__item">
              <div className="pf-timeline__marker">3</div>
              <div className="pf-timeline__content">
                <h4>Use if you lose capacity</h4>
                <p>
                  If you lose mental capacity, your attorneys may act on your behalf without needing
                  your consent. This ensures that your financial affairs continue to be managed
                  without disruption.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risks of Not Having One */}
      <section className="pf-section pf-section--alt">
        <div className="pf-section__inner">
          <p className="pf-section__label">Why It Matters</p>
          <h2 className="pf-section__title">Risks of Not Having One</h2>
          <p className="pf-section__desc">
            Without a Property and Financial Affairs LPA in place, the consequences of losing
            mental capacity can be significant and far-reaching:
          </p>
          <div className="pf-cards">
            <div className="pf-card">
              <div className="pf-card__title">Assets frozen</div>
              <div className="pf-card__text">
                Banks and financial institutions may freeze your accounts, preventing access to
                funds needed for your care and living expenses.
              </div>
            </div>
            <div className="pf-card">
              <div className="pf-card__title">Property sales delayed</div>
              <div className="pf-card__text">
                Selling or managing your property becomes impossible without the proper legal
                authority, causing delays and potential financial loss.
              </div>
            </div>
            <div className="pf-card">
              <div className="pf-card__title">Bills unpaid</div>
              <div className="pf-card__text">
                Household bills, mortgage payments and other liabilities may go unpaid, leading to
                debt, penalties and potential legal proceedings.
              </div>
            </div>
            <div className="pf-card">
              <div className="pf-card__title">Deputyship required</div>
              <div className="pf-card__text">
                A family member or professional would need to apply to the Court of Protection for a
                deputyship order, which is more costly, time-consuming and restrictive than an LPA.
              </div>
            </div>
          </div>
          <div className="pf-note">
            <p>
              A deputyship order is more restrictive than an LPA. Deputies are subject to ongoing
              supervision by the OPG, must submit annual reports, and may need court approval for
              certain decisions. The process of obtaining a deputyship order is also considerably
              more expensive and can take many months.
            </p>
          </div>
        </div>
      </section>

      {/* Cancelling a Financial LPA */}
      <section className="pf-section pf-section--white">
        <div className="pf-section__inner">
          <p className="pf-section__label">Revocation</p>
          <h2 className="pf-section__title">Cancelling a Financial LPA</h2>
          <p className="pf-section__desc">
            You may revoke your Property and Financial Affairs LPA at any time, provided you
            retain the mental capacity to do so. Revocation must be carried out formally to
            ensure that all relevant parties are notified and that the LPA is no longer relied
            upon.
          </p>
          <ul className="pf-list">
            <li>
              You must execute a formal Deed of Revocation, which is a written document confirming
              your intention to cancel the LPA.
            </li>
            <li>
              The Office of the Public Guardian must be notified so that the registration is
              cancelled.
            </li>
            <li>
              Your attorneys should be informed in writing that their authority has been revoked.
            </li>
            <li>
              Any third parties who have been relying on the LPA (such as banks and financial
              institutions) should also be notified.
            </li>
          </ul>
          <div className="pf-note">
            <p>
              If you wish to revoke your LPA, we recommend seeking legal advice to ensure the
              process is carried out correctly and that your interests are fully protected.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pf-cta">
        <div className="pf-cta__inner">
          <h2 className="pf-cta__title">Protect Your Financial Affairs</h2>
          <p className="pf-cta__desc">
            Speak with us today about putting a Property and Financial Affairs LPA in place.
            We will guide you through every step of the process.
          </p>
          <Link to="/contact" className="btn btn-primary">
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PropertyFinancial;
